import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCampaignPermissions } from '../../../hooks/useCampaignPermissions';
import {
  createCampaignList,
  downloadCampaignReport,
  fetchCampaign,
  fetchCampaignCompliance,
  fetchCampaignLeads,
  fetchCampaignLists,
  fetchCampaignReport,
  fetchCampaignStats,
  importCampaignList,
  updateCampaignList,
} from '../../../services/callcenterV2/campaignsService';
import realtimeService from '../../../services/callcenterV2/realtimeService';
import { buildManualImportFile, downloadCampaignTemplate } from '../../../services/callcenterV2/csvUtils';
import {
  saoPauloDayEndUtcIso,
  saoPauloDayStartUtcIso,
  toSaoPauloDateString,
} from '../../../utils/datetime';
import CsvUploadZone from '../../../components/CallcenterV2/CsvUploadZone';
import '../../../components/CallcenterV2/ccv2-components.css';
import '../config-styles.css';

const STATS_POLL_MS = 30_000;
const LEADS_POLL_MS = 8_000;
const DAYS_PT = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const TABS = [
  { key: 'realtime', label: 'Tempo real' },
  { key: 'lists', label: 'Listas' },
  { key: 'report', label: 'Relatório' },
  { key: 'compliance', label: 'Compliance' },
];

const DISPOSITION_LABELS = {
  answered: 'Atendida',
  busy: 'Ocupado',
  noanswer: 'Não atendeu',
  congestion: 'Congestionamento',
  failed: 'Falha',
  abandon: 'Abandono na fila',
  answering_machine: 'Secretária eletrônica',
  opt_out: 'Opt-out',
  stale: 'Órfã (reconciliada)',
  window_closed: 'Fora de janela',
  pending: 'Em andamento',
};

const REJECTION_REASONS = {
  empty: 'Vazio',
  too_short: 'Muito curto',
  too_long: 'Muito longo',
  invalid_ddd: 'DDD inválido',
  invalid_subscriber: 'Assinante inválido',
  not_brazilian: 'Não é número brasileiro',
};

const percent = (value) => `${((Number(value) || 0) * 100).toFixed(1)}%`;
const seconds = (value) => {
  const total = Number(value) || 0;
  const min = Math.floor(total / 60);
  const sec = total % 60;
  return `${min}m ${String(sec).padStart(2, '0')}s`;
};

function Tile({ label, value, hint, tone }) {
  const toneClass = tone ? ` ccv2-stat-tile--${tone}` : '';
  return (
    <div className={`ccv2-stat-tile${toneClass}`}>
      <span className="ccv2-stat-tile-label">{label}</span>
      <span className="ccv2-stat-tile-value">{value}</span>
      {hint && <span className="ccv2-stat-tile-hint">{hint}</span>}
    </div>
  );
}

function RealtimeTab({ campaignId, campaign }) {
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(null);
  const [live, setLive] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setStats(await fetchCampaignStats(campaignId));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [campaignId]);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, STATS_POLL_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  useEffect(() => {
    const unsubscribeStats = realtimeService.subscribeEvent('campaign.stats', (event) => {
      if (Number(event?.payload?.idDialerCampaign) !== Number(campaignId)) return;
      setStats((prev) => ({ ...(prev || {}), ...event.payload }));
    });
    const unsubscribeConnected = realtimeService.subscribeConnected(setLive);
    return () => { unsubscribeStats(); unsubscribeConnected(); };
  }, [campaignId]);

  if (error) return <div className="ccv2-p5-error">⚠️ {error}</div>;
  if (!stats) return <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>;

  return (
    <>
      <div className="ccv2-inline-meta">
        <span className={`ccv2-badge ${live ? 'ccv2-badge--success' : 'ccv2-badge--muted'}`}>
          {live ? '● tempo real' : '○ polling 30s'}
        </span>
        {stats.gateReason && <span className="ccv2-badge ccv2-badge--warn">freio: {stats.gateReason}</span>}
        <span>atualizado {stats.ts ? new Date(stats.ts).toLocaleTimeString('pt-BR') : '-'}</span>
      </div>

      <div className="ccv2-stat-tiles">
        <Tile label="Discagens" value={stats.dials ?? 0} hint={`${stats.callsPerHour ?? 0}/h na última hora`} />
        <Tile label="Atendidas" value={stats.answers ?? 0} hint={`contato ${percent(stats.contactRate)}`} />
        <Tile label="Conectadas" value={stats.connects ?? 0} hint={`TMA ${seconds(stats.averageTalkSeconds)}`} />
        <Tile
          label="Abandonos"
          value={stats.drops ?? 0}
          hint={`${percent(stats.dropRate)} das atendidas`}
          tone={(stats.dropRate ?? 0) > 0.03 ? 'danger' : undefined}
        />
        <Tile label="Chamadas curtas" value={stats.shortCalls ?? 0} tone={(stats.shortCalls ?? 0) > 0 ? 'warn' : undefined} />
        <Tile label="Em voo" value={stats.inFlight ?? 0} hint={`hopper ${stats.hopperSize ?? 0}`} />
        <Tile
          label="Leads restantes"
          value={stats.leadsRemaining ?? 0}
          hint={`${stats.leadsDone ?? 0} concluídos de ${stats.leadsTotal ?? 0}`}
        />
        <Tile label="Penetração" value={percent(stats.penetrationRate)} hint="leads já tentados" />
      </div>

      {campaign?.dialMode === 'power' && campaign?.status === 'running' && (stats.inFlight ?? 0) === 0 && (
        <div className="ccv2-p5-error ccv2-stack-top">
          Campanha rodando sem chamadas em voo. Verifique agentes livres na fila, o freio de compliance e a flag do discador.
        </div>
      )}

      <CampaignLeadsCard campaignId={campaignId} live={live} />
    </>
  );
}

function CampaignLeadsCard({ campaignId, live }) {
  const [items, setItems] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  useEffect(() => {
    const handle = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(handle);
  }, [search]);

  const load = useCallback(async (cursor) => {
    try {
      const data = await fetchCampaignLeads(campaignId, {
        state: filter === 'all' ? undefined : filter,
        q: q || undefined,
        limit: 50,
        cursor,
      });
      setItems((prev) => (cursor ? [...prev, ...(data.items || [])] : data.items || []));
      setNextCursor(data.nextCursor ?? null);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId, filter, q]);

  useEffect(() => {
    setLoading(true);
    load();
  }, [load]);

  useEffect(() => {
    if (live) {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      return undefined;
    }
    timerRef.current = setInterval(() => load(), LEADS_POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [live, load]);

  useEffect(() => {
    if (!live) return undefined;
    const unsubscribe = realtimeService.subscribeEvent('dialer.attempt', (event) => {
      if (Number(event?.payload?.idDialerCampaign) !== Number(campaignId)) return;
      load();
    });
    return unsubscribe;
  }, [live, campaignId, load]);

  return (
    <div className="ccv2-config-card ccv2-card-gap ccv2-stack-top">
      <div className="ccv2-card-title">
        <div>
          <h3>Contatos da campanha</h3>
          <p>Nome, telefone e situação de discagem. A lista não inclui o CSV original.</p>
        </div>
      </div>
      <div className="ccv2-card-body">
        <div className="ccv2-filters">
          <div className="ccv2-filter-group">
            <label htmlFor="ccv2-leads-filter">Situação</label>
            <select
              id="ccv2-leads-filter"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="undialed">Não discado</option>
              <option value="dialed">Discado</option>
            </select>
          </div>
          <div className="ccv2-filter-group">
            <label htmlFor="ccv2-leads-q">Busca</label>
            <input
              id="ccv2-leads-q"
              type="search"
              value={search}
              placeholder="Nome ou telefone"
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && <div className="ccv2-p5-error ccv2-stack-top">⚠️ {error}</div>}

        {loading && items.length === 0 ? (
          <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>
        ) : (
          <div className="ccv2-config-table-wrapper ccv2-stack-top">
            <table className="ccv2-config-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Telefone</th>
                  <th>Situação</th>
                  <th>Último resultado</th>
                  <th>Próxima tentativa</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="ccv2-empty-row">Nenhum contato nesta visão</td>
                  </tr>
                ) : (
                  items.map((lead) => (
                    <tr key={lead.idDialerLead}>
                      <td>{lead.name || '—'}</td>
                      <td className="ccv2-cell-phone">{lead.phone || '—'}</td>
                      <td>{lead.stateLabel}</td>
                      <td>{DISPOSITION_LABELS[lead.lastDisposition] || lead.lastDisposition || '—'}</td>
                      <td>
                        {lead.nextAttemptAt
                          ? new Date(lead.nextAttemptAt).toLocaleString('pt-BR')
                          : '—'}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {nextCursor !== null && (
          <div className="ccv2-filter-actions ccv2-stack-top">
            <button type="button" className="ccv2-btn ccv2-btn-outline" onClick={() => load(nextCursor)}>
              Carregar mais
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function ImportSummaryCard({ summary }) {
  const samples = Array.isArray(summary.rejectionSamples) ? summary.rejectionSamples : [];

  return (
    <div className="ccv2-config-card ccv2-card-gap">
      <div className="ccv2-card-title">
        <div>
          <h3>Resultado do último import</h3>
          <p>
            Lista #{summary.idDialerCampaignList} · {summary.totalRows} linhas lidas · separador
            {summary.delimiter === '\t' ? ' tabulação' : ` "${summary.delimiter}"`}
          </p>
        </div>
      </div>
      <div className="ccv2-card-body">
        <div className="ccv2-stat-tiles">
          <Tile label="Leads importados" value={summary.importedLeads ?? 0} tone="success" />
          <Tile label="Telefones" value={summary.importedPhones ?? 0} />
          <Tile
            label="Rejeitados"
            value={summary.rejectedRows ?? 0}
            hint="linhas sem telefone válido"
            tone={(summary.rejectedRows ?? 0) > 0 ? 'danger' : undefined}
          />
          <Tile label="Duplicados" value={summary.duplicateRows ?? 0} hint="já existiam na campanha" />
          <Tile
            label="Bloqueados (DNC)"
            value={summary.dncRejectedRows ?? 0}
            tone={(summary.dncRejectedRows ?? 0) > 0 ? 'warn' : undefined}
          />
        </div>

        {summary.truncated && (
          <div className="ccv2-p5-error ccv2-stack-top">
            Arquivo truncado no limite de 200.000 linhas. Divida a base em lotes menores.
          </div>
        )}

        {samples.length > 0 && (
          <div className="ccv2-stack-top">
            <p className="ccv2-hint">Amostra dos telefones recusados (máximo 20):</p>
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead><tr><th>Linha</th><th>Motivo</th><th>Valor</th></tr></thead>
                <tbody>
                  {samples.map((sample, index) => (
                    <tr key={`${sample.line}-${index}`}>
                      <td>{sample.line}</td>
                      <td>{REJECTION_REASONS[sample.reason] || sample.reason}</td>
                      <td className="ccv2-cell-phone">{sample.value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ImportCard({ campaignId, lists, canManage, onImported, onFailed }) {
  const [target, setTarget] = useState('new');
  const [newListName, setNewListName] = useState('');
  const [file, setFile] = useState(null);
  const [manual, setManual] = useState('');
  const [importing, setImporting] = useState(false);
  const pendingListRef = useRef(null);

  useEffect(() => {
    setTarget((prev) => {
      if (prev !== 'new' && lists.some((list) => String(list.idDialerCampaignList) === prev)) return prev;
      return lists.length > 0 ? String(lists[0].idDialerCampaignList) : 'new';
    });
  }, [lists]);

  const hasManual = manual.trim().length > 0;
  const canImport =
    canManage &&
    !importing &&
    (Boolean(file) || hasManual) &&
    (target !== 'new' || newListName.trim().length > 0);

  const runImport = async () => {
    setImporting(true);
    try {
      let listId = target;
      if (target === 'new') {
        if (pendingListRef.current) {
          listId = pendingListRef.current;
        } else {
          const created = await createCampaignList(campaignId, { name: newListName.trim() });
          listId = created.idDialerCampaignList;
          pendingListRef.current = listId;
        }
      }
      const payload = file || buildManualImportFile(manual);
      const result = await importCampaignList(campaignId, listId, payload);
      pendingListRef.current = null;
      setFile(null);
      setManual('');
      setNewListName('');
      setTarget(String(listId));
      onImported(result);
    } catch (e) {
      onFailed(e);
    } finally {
      setImporting(false);
    }
  };

  if (!canManage) return null;

  return (
    <div className="ccv2-config-card ccv2-card-gap">
      <div className="ccv2-card-title">
        <div>
          <h3>Importar contatos</h3>
          <p>Envie um CSV ou cole os números: validamos DDD, removemos duplicados e descartamos bloqueados.</p>
        </div>
        <button type="button" className="ccv2-btn ccv2-btn-outline ccv2-btn-sm" onClick={downloadCampaignTemplate}>
          ⬇ Baixar modelo CSV
        </button>
      </div>

      <div className="ccv2-card-body">
        <div className="ccv2-form-row">
          <div className="ccv2-form-group">
            <label htmlFor="ccv2-import-target">Lista de destino</label>
            <select
              id="ccv2-import-target"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              disabled={importing}
            >
              {lists.map((list) => (
                <option key={list.idDialerCampaignList} value={String(list.idDialerCampaignList)}>
                  {list.name}
                </option>
              ))}
              <option value="new">Criar nova lista...</option>
            </select>
          </div>
          <div className="ccv2-form-group">
            <label htmlFor="ccv2-import-new-list">Nome da nova lista</label>
            <input
              id="ccv2-import-new-list"
              value={newListName}
              onChange={(e) => setNewListName(e.target.value)}
              maxLength={120}
              placeholder="ex.: Lote 1 - setembro"
              disabled={target !== 'new' || importing}
            />
          </div>
        </div>

        <div className="ccv2-form-group">
          <label>Arquivo CSV</label>
          <CsvUploadZone
            accept=".csv,.txt,text/csv,text/plain"
            file={file}
            onFile={setFile}
            disabled={importing || hasManual}
            hint="CSV UTF-8 até 20 MB · 200.000 linhas por arquivo"
          />
        </div>

        <div className="ccv2-form-group">
          <label htmlFor="ccv2-import-manual">Ou cole os números (um por linha)</label>
          <textarea
            id="ccv2-import-manual"
            rows={4}
            value={manual}
            onChange={(e) => setManual(e.target.value)}
            disabled={Boolean(file) || importing}
            placeholder={'11999998888\n1133334444'}
          />
          <p className="ccv2-hint">
            Colunas aceitas no CSV: <code>telefone</code> (obrigatória), <code>telefone2</code>, <code>telefone3</code>,
            <code> nome</code>, <code>external_id</code>. Salve como CSV UTF-8 para não corromper acentos.
            O arquivo é descartado após o import (LGPD): ficam apenas hash e contagens.
          </p>
        </div>

        <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={runImport} disabled={!canImport}>
          {importing ? 'Importando...' : '⬆ Importar contatos'}
        </button>
      </div>
    </div>
  );
}

function ListsTab({ campaignId, canManage }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [summary, setSummary] = useState(null);
  const [uploading, setUploading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCampaignLists(campaignId);
      setLists(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const unsubscribe = realtimeService.subscribeEvent('campaign.list.import_progress', (event) => {
      if (Number(event?.payload?.idDialerCampaign) !== Number(campaignId)) return;
      load();
    });
    return unsubscribe;
  }, [campaignId, load]);

  const handleImported = useCallback((result) => {
    setSummary(result);
    setError(null);
    setSuccess(
      `Importados ${result.importedLeads ?? 0} leads e ${result.importedPhones ?? 0} telefones.`,
    );
    load();
  }, [load]);

  const handleFailed = useCallback((e) => {
    setSuccess(null);
    setError(
      e.status === 409
        ? e.message || 'Conflito ao importar: a lista já está em importação ou o arquivo já foi enviado.'
        : e.status === 413
          ? 'Arquivo grande demais para o servidor web. Divida a base em lotes menores.'
          : e.message,
    );
  }, []);

  const uploadToList = async (listId, file) => {
    if (!file) return;
    setUploading(listId);
    try {
      handleImported(await importCampaignList(campaignId, listId, file));
    } catch (e) {
      handleFailed(e);
    } finally {
      setUploading(null);
    }
  };

  const toggleStatus = async (list) => {
    try {
      await updateCampaignList(campaignId, list.idDialerCampaignList, {
        status: list.status === 'active' ? 'paused' : 'active',
      });
      load();
    } catch (e) {
      setError(e.message);
    }
  };

  if (loading) return <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>;

  return (
    <>
      {success && <div className="ccv2-p5-success">{success}</div>}
      {error && <div className="ccv2-p5-error">⚠️ {error}</div>}

      <ImportCard
        campaignId={campaignId}
        lists={lists}
        canManage={canManage}
        onImported={handleImported}
        onFailed={handleFailed}
      />

      {summary && <ImportSummaryCard summary={summary} />}

      <div className="ccv2-config-card">
        <div className="ccv2-card-title">
          <div>
            <h3>Listas da campanha</h3>
            <p>O discador consome as listas ativas por ordem de prioridade.</p>
          </div>
        </div>
        <div className="ccv2-config-table-wrapper">
          <table className="ccv2-config-table">
            <thead>
              <tr>
                <th>Lista</th>
                <th>Status</th>
                <th>Importação</th>
                <th>Leads</th>
                <th>Rejeitados</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lists.length === 0 ? (
                <tr><td colSpan={6} className="ccv2-empty-row">Nenhuma lista nesta campanha.</td></tr>
              ) : lists.map((list) => (
                <tr key={list.idDialerCampaignList}>
                  <td className="ccv2-cell-name">
                    {list.name}
                    <span className="ccv2-cell-secondary">prioridade {list.priority}</span>
                  </td>
                  <td>
                    <span className={`ccv2-badge ${list.status === 'active' ? 'ccv2-badge--success' : 'ccv2-badge--muted'}`}>
                      {list.status === 'active' ? 'Ativa' : 'Pausada'}
                    </span>
                  </td>
                  <td>
                    {list.importStatus}
                    {list.importError && <span className="ccv2-cell-error">{list.importError}</span>}
                    {list.sourceFilename && <span className="ccv2-cell-secondary">{list.sourceFilename}</span>}
                  </td>
                  <td>
                    {list.importedLeads}
                    <span className="ccv2-cell-secondary">{list.importedPhones} fones</span>
                  </td>
                  <td>
                    {list.rejectedRows} inválidos
                    <span className="ccv2-cell-secondary">{list.duplicateRows} dup · {list.dncRejectedRows} DNC</span>
                  </td>
                  <td>
                    {canManage && (
                      <div className="ccv2-cell-actions">
                        <label className="ccv2-btn ccv2-btn-sm ccv2-btn-primary ccv2-inline-upload">
                          {uploading === list.idDialerCampaignList ? '⌛ Enviando' : '⬆ CSV'}
                          <input
                            type="file"
                            accept=".csv,.txt,text/csv,text/plain"
                            disabled={uploading !== null}
                            onChange={(e) => { uploadToList(list.idDialerCampaignList, e.target.files[0]); e.target.value = ''; }}
                          />
                        </label>
                        <button
                          type="button"
                          className="ccv2-btn ccv2-btn-sm ccv2-btn-secondary"
                          onClick={() => toggleStatus(list)}
                        >
                          {list.status === 'active' ? '⏸ Pausar' : '▶ Ativar'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function ReportTab({ campaignId }) {
  const today = new Date();
  const weekAgo = new Date(today.getTime() - 7 * 86_400_000);
  const [range, setRange] = useState({
    from: toSaoPauloDateString(weekAgo),
    to: toSaoPauloDateString(today),
  });
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const period = useMemo(() => {
    const from = saoPauloDayStartUtcIso(range.from);
    const to = saoPauloDayEndUtcIso(range.to);
    if (!from || !to) return null;
    return { from, to };
  }, [range]);

  const load = useCallback(async () => {
    if (!period) {
      setLoading(false);
      setError('Informe as datas inicial e final do período.');
      return;
    }
    setLoading(true);
    try {
      setReport(await fetchCampaignReport(campaignId, period));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId, period]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    if (!period) {
      setError('Informe as datas inicial e final do período.');
      return;
    }
    setExporting(true);
    try {
      const result = await downloadCampaignReport(campaignId, period);
      if (result.truncated) setError(`Export limitado a ${result.rows} linhas. Reduza o período.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <div className="ccv2-filters">
        <div className="ccv2-filter-group">
          <label htmlFor="ccv2-report-from">De</label>
          <input
            id="ccv2-report-from"
            type="date"
            value={range.from}
            onChange={(e) => setRange((p) => ({ ...p, from: e.target.value }))}
          />
        </div>
        <div className="ccv2-filter-group">
          <label htmlFor="ccv2-report-to">Até</label>
          <input
            id="ccv2-report-to"
            type="date"
            value={range.to}
            onChange={(e) => setRange((p) => ({ ...p, to: e.target.value }))}
          />
        </div>
        <button type="button" className="ccv2-btn ccv2-btn-secondary" onClick={load}>🔄 Atualizar</button>
        <button type="button" className="ccv2-btn ccv2-btn-primary" onClick={exportCsv} disabled={exporting}>
          {exporting ? 'Exportando...' : '⬇ Exportar CSV'}
        </button>
      </div>

      {error && <div className="ccv2-p5-error">⚠️ {error}</div>}
      {loading || !report ? <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div> : (
        <>
          <div className="ccv2-stat-tiles ccv2-card-gap">
            <Tile label="Discagens" value={report.funnel.dials} hint={`${report.funnel.uniqueLeads} leads únicos`} />
            <Tile label="Chamadas atendidas" value={report.funnel.answered} hint={`contato ${percent(report.funnel.contactRate)}`} />
            <Tile label="Conectadas a agente" value={report.funnel.connected} hint={`conversão ${percent(report.funnel.conversionRate)}`} />
            <Tile label="Abandonos" value={report.funnel.abandoned} hint={percent(report.funnel.dropRate)} tone={report.funnel.dropRate > 0.03 ? 'danger' : undefined} />
            <Tile label="Tempo falado" value={seconds(report.funnel.talkSeconds)} />
          </div>

          <div className="ccv2-config-card ccv2-card-gap">
            <div className="ccv2-card-title"><h3>Disposições</h3></div>
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead><tr><th>Disposição</th><th>Tentativas</th><th>Participação</th></tr></thead>
                <tbody>
                  {report.dispositions.length === 0 ? (
                    <tr><td colSpan={3} className="ccv2-p5-empty">Sem tentativas no período.</td></tr>
                  ) : report.dispositions.map((bucket) => (
                    <tr key={bucket.disposition}>
                      <td>{DISPOSITION_LABELS[bucket.disposition] || bucket.disposition}</td>
                      <td>{bucket.attempts}</td>
                      <td>{percent(bucket.share)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ccv2-config-card ccv2-card-gap">
            <div className="ccv2-card-title"><h3>Por lista</h3></div>
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead><tr><th>Lista</th><th>Leads</th><th>Tentativas</th><th>Atendidas</th><th>Conectadas</th></tr></thead>
                <tbody>
                  {report.byList.length === 0 ? (
                    <tr><td colSpan={5} className="ccv2-p5-empty">Sem listas com leads.</td></tr>
                  ) : report.byList.map((bucket) => (
                    <tr key={bucket.idDialerCampaignList ?? 'sem-lista'}>
                      <td>{bucket.listName || 'Sem lista'}</td>
                      <td>{bucket.leads}</td>
                      <td>{bucket.attempts}</td>
                      <td>{bucket.answered}</td>
                      <td>{bucket.connected}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="ccv2-config-card">
            <div className="ccv2-card-title"><h3>Por agente</h3></div>
            <div className="ccv2-config-table-wrapper">
              <table className="ccv2-config-table">
                <thead><tr><th>Agente</th><th>Conectadas</th><th>Tempo falado</th><th>Curtas</th></tr></thead>
                <tbody>
                  {report.byAgent.length === 0 ? (
                    <tr><td colSpan={4} className="ccv2-p5-empty">Nenhuma chamada conectada a agente no período.</td></tr>
                  ) : report.byAgent.map((bucket) => (
                    <tr key={bucket.agent}>
                      <td>{bucket.agent}</td>
                      <td>{bucket.connected}</td>
                      <td>{seconds(bucket.talkSeconds)}</td>
                      <td>{bucket.shortCalls}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}

function ComplianceTab({ campaignId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await fetchCampaignCompliance(campaignId, { days: 14 }));
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const unsubscribe = realtimeService.subscribeEvent('dialer.compliance.', () => load());
    return unsubscribe;
  }, [load]);

  if (loading || !data) {
    return error ? <div className="ccv2-p5-error">⚠️ {error}</div> : <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>;
  }

  const ratio = data.today.shortCallRatio;
  const ratioTone = ratio >= data.thresholds.shortCallStopRatio ? 'danger' : ratio >= data.thresholds.shortCallThrottleRatio ? 'warn' : undefined;
  const monthlyTone = data.monthly.severity === 'critical' ? 'danger' : data.monthly.severity === 'warning' ? 'warn' : undefined;

  return (
    <>
      {error && <div className="ccv2-p5-error">⚠️ {error}</div>}

      {data.brake.engaged && (
        <div className="ccv2-p5-error">
          🛑 Freio de compliance ativo no tronco <strong>{data.trunkName}</strong> (
          {data.brake.level === 'full' ? 'parada total' : 'CPS reduzido'}
          {data.brake.reason ? ` · ${data.brake.reason}` : ''}
          {data.brake.engagedAt ? ` desde ${new Date(data.brake.engagedAt).toLocaleString('pt-BR')}` : ''}).
        </div>
      )}

      <div className="ccv2-stat-tiles ccv2-card-gap">
        <Tile
          label="Chamadas curtas hoje"
          value={percent(ratio)}
          hint={`${data.today.shortCalls} de ${data.today.totalCalls} · freio em ${percent(data.thresholds.shortCallThrottleRatio)}`}
          tone={ratioTone}
        />
        <Tile label="Atendidas hoje" value={data.today.answeredCalls} hint={`${data.today.abandonedCalls} abandonos`} />
        <Tile
          label={`Chamadas em ${data.monthly.month}`}
          value={data.monthly.totalCalls}
          hint={`alerta em ${data.thresholds.monthlyWarningCalls} (fonte: ${data.monthly.source})`}
          tone={monthlyTone}
        />
        <Tile label="Janelas por UF" value={data.respectUfWindows ? 'Ativas' : 'Desligadas'} hint={data.trunkName} />
      </div>

      <div className="ccv2-config-card ccv2-card-gap">
        <div className="ccv2-card-title"><h3>Histórico diário do tronco</h3></div>
        <div className="ccv2-config-table-wrapper">
          <table className="ccv2-config-table">
            <thead><tr><th>Dia</th><th>Total</th><th>Atendidas</th><th>Curtas</th><th>% curtas</th><th>Abandonos</th></tr></thead>
            <tbody>
              {data.daily.length === 0 ? (
                <tr><td colSpan={6} className="ccv2-p5-empty">Sem consolidação nos últimos dias.</td></tr>
              ) : data.daily.map((row) => {
                const dayRatio = row.totalCalls > 0 ? row.shortCalls / row.totalCalls : 0;
                return (
                  <tr key={row.day}>
                    <td>{row.day}</td>
                    <td>{row.totalCalls}</td>
                    <td>{row.answeredCalls}</td>
                    <td>{row.shortCalls}</td>
                    <td style={{ color: dayRatio >= data.thresholds.shortCallThrottleRatio ? '#dc2626' : undefined }}>
                      {percent(dayRatio)}
                    </td>
                    <td>{row.abandonedCalls}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ccv2-config-card">
        <div className="ccv2-card-title"><h3>Janelas de discagem configuradas</h3></div>
        <div className="ccv2-config-table-wrapper">
          <table className="ccv2-config-table">
            <thead><tr><th>Escopo</th><th>UF</th><th>Dia</th><th>Início</th><th>Fim</th></tr></thead>
            <tbody>
              {data.callingWindows.length === 0 ? (
                <tr><td colSpan={5} className="ccv2-p5-empty">Nenhuma janela configurada (discagem sem restrição de horário).</td></tr>
              ) : data.callingWindows.map((window, index) => (
                <tr key={`${window.scope}-${window.uf}-${window.weekday}-${index}`}>
                  <td>{window.scope === 'global' ? 'Padrão global' : 'Tenant'}</td>
                  <td>{window.uf === '*' ? 'Todas' : window.uf}</td>
                  <td>{DAYS_PT[window.weekday] || window.weekday}</td>
                  <td>{window.startTime}</td>
                  <td>{window.endTime}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

function CampaignDetail() {
  const { id } = useParams();
  const campaignId = Number(id);
  const { canManage } = useCampaignPermissions();

  const [campaign, setCampaign] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('realtime');

  const load = useCallback(async () => {
    try {
      setCampaign(await fetchCampaign(campaignId));
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, [campaignId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const unsubscribe = realtimeService.subscribeEvent('campaign.status_changed', (event) => {
      if (Number(event?.payload?.idDialerCampaign) === campaignId) load();
    });
    return unsubscribe;
  }, [campaignId, load]);

  if (error) return <div className="ccv2-page-container"><div className="ccv2-p5-error">⚠️ {error}</div></div>;
  if (!campaign) return <div className="ccv2-config-loading"><div className="ccv2-spinner" /></div>;

  return (
    <div className="ccv2-page-container ccv2-p5-container">
      <div className="ccv2-page-header">
        <div className="ccv2-page-title-area">
          <h1>{campaign.name}</h1>
          <p>
            {campaign.status} · fila {campaign.queueName} · tronco {campaign.trunkName}
            {campaign.callerId ? ` · CLI ${campaign.callerId}` : ''}
          </p>
        </div>
        <div className="ccv2-page-actions">
          <Link className="ccv2-btn ccv2-btn-secondary" to="/callcenterV2/campaigns">← Campanhas</Link>
        </div>
      </div>

      <div className="ccv2-tabs">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`ccv2-tab ${tab === item.key ? 'active' : ''}`}
            onClick={() => setTab(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'realtime' && <RealtimeTab campaignId={campaignId} campaign={campaign} />}
      {tab === 'lists' && <ListsTab campaignId={campaignId} canManage={canManage} />}
      {tab === 'report' && <ReportTab campaignId={campaignId} />}
      {tab === 'compliance' && <ComplianceTab campaignId={campaignId} />}
    </div>
  );
}

export default CampaignDetail;
